import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Tutors = () => {
  const [tutors, setTutors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTutor, setEditingTutor] = useState(null);
  const [viewingTutor, setViewingTutor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    gender: '',
    age: '',
    area: '',
    locality: '',
    address: '',
    mobileNumbers: [''],
    educationalQualifications: '',
    teachingExperience: '',
    fluencyInEnglish: 'Good',
    preferredTime: '',
    preferredSubjects: [''],
    preferredBoards: [],
    preferredClasses: [''],
    ownVehicle: false,
    areaCategory: '',
    rating: '',
    registrationFeesPaid: false,
    dateOfJoining: '',
    kycDetails: {
      formSubmitted: false,
      passportPhoto: false,
      aadhaarFront: false,
      aadhaarBack: false
    },
    remarks: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchTutors();
  }, []);

  const fetchTutors = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/tutor-finals');
      setTutors(response.data.tutors || []);
    } catch (error) {
      console.error('Error fetching tutors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleArrayChange = (index, field, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const addArrayField = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const removeArrayField = (index, field) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray });
  };

  const handleBoardChange = (board) => {
    const boards = formData.preferredBoards.includes(board)
      ? formData.preferredBoards.filter(b => b !== board)
      : [...formData.preferredBoards, board];
    setFormData({ ...formData, preferredBoards: boards });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    const cleanedData = {
      ...formData,
      mobileNumbers: formData.mobileNumbers.filter(m => m.trim()),
      preferredSubjects: formData.preferredSubjects.filter(s => s.trim()),
      preferredClasses: formData.preferredClasses.filter(c => c.trim())
    };

    try {
      if (editingTutor) {
        await axios.put(`/api/tutor-finals/${editingTutor._id}`, cleanedData);
        setMessage('Tutor updated successfully!');
      } else {
        await axios.post('/api/tutor-finals', cleanedData);
        setMessage('Tutor added successfully!');
      }

      resetForm();
      fetchTutors();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving tutor:', error);
      setMessage(error.response?.data?.message || 'Error saving tutor');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (tutor) => {
    setEditingTutor(tutor);
    setFormData({
      ...tutor,
      mobileNumbers: tutor.mobileNumbers?.length ? tutor.mobileNumbers : [''],
      preferredSubjects: tutor.preferredSubjects?.length ? tutor.preferredSubjects : [''],
      preferredClasses: tutor.preferredClasses?.length ? tutor.preferredClasses : [''],
      preferredBoards: tutor.preferredBoards || [],
      dateOfJoining: tutor.dateOfJoining?.split('T')[0] || '',
      kycDetails: tutor.kycDetails || {
        formSubmitted: false,
        passportPhoto: false,
        aadhaarFront: false,
        aadhaarBack: false
      }
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tutor?')) return;

    try {
      await axios.delete(`/api/tutor-finals/${id}`);
      setMessage('Tutor deleted successfully!');
      fetchTutors();
    } catch (error) {
      console.error('Error deleting tutor:', error);
      setMessage('Error deleting tutor');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      gender: '',
      age: '',
      area: '',
      locality: '',
      address: '',
      mobileNumbers: [''],
      educationalQualifications: '',
      teachingExperience: '',
      fluencyInEnglish: 'Good',
      preferredTime: '',
      preferredSubjects: [''],
      preferredBoards: [],
      preferredClasses: [''],
      ownVehicle: false,
      areaCategory: '',
      rating: '',
      registrationFeesPaid: false,
      dateOfJoining: '',
      kycDetails: {
        formSubmitted: false,
        passportPhoto: false,
        aadhaarFront: false,
        aadhaarBack: false
      },
      remarks: ''
    });
    setEditingTutor(null);
  };

  return (
    <div className="container" style={{ padding: '32px 24px' }}>
      <div className="page-header">
        <h1>Finalized Tutors</h1>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          + Add New Tutor
        </button>
      </div>

      {message && (
        <div className={`alert ${message.includes('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}
        </div>
      )}

      <div className="card">
        <h3>Tutors List ({tutors.length})</h3>
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Gender</th>
                  <th>Area</th>
                  <th>Mobile Numbers</th>
                  <th>Rating</th>
                  <th>Total Students</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tutors.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>No tutors found</td>
                  </tr>
                ) : (
                  tutors.map((tutor) => (
                    <tr key={tutor._id}>
                      <td>{tutor.name}</td>
                      <td>{tutor.gender || 'N/A'}</td>
                      <td>{tutor.area || 'N/A'}</td>
                      <td>{tutor.mobileNumbers?.join(', ')}</td>
                      <td>{tutor.rating || 'N/A'}</td>
                      <td>{tutor.students?.length || 0}</td>
                      <td className="actions">
                        <button className="btn btn-secondary" onClick={() => setViewingTutor(tutor)} style={{ marginRight: '8px' }}>
                          View Students
                        </button>
                        <button className="btn btn-secondary" onClick={() => handleEdit(tutor)} style={{ marginRight: '8px' }}>
                          Edit
                        </button>
                        <button className="btn btn-danger" onClick={() => handleDelete(tutor._id)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2>{editingTutor ? 'Edit' : 'Add'} Tutor</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Gender</label>
                  <select name="gender" value={formData.gender} onChange={handleChange}>
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Age</label>
                  <input type="number" name="age" value={formData.age} onChange={handleChange} min="18" max="100" />
                </div>
                <div className="form-group">
                  <label>Area</label>
                  <input type="text" name="area" value={formData.area} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Locality</label>
                  <input type="text" name="locality" value={formData.locality} onChange={handleChange} placeholder="Specific locality within area" />
                </div>
                <div className="form-group">
                  <label>Area Category</label>
                  <select name="areaCategory" value={formData.areaCategory} onChange={handleChange}>
                    <option value="">Select Area Category</option>
                    <optgroup label="Class 1-10 (8 Areas)">
                      <option value="Wardhman Nagar-Wathoda">Wardhman Nagar-Wathoda</option>
                      <option value="Nandanvan-Dighori">Nandanvan-Dighori</option>
                      <option value="Itwari-Medical">Itwari-Medical</option>
                      <option value="Manewada-Manish Nagar">Manewada-Manish Nagar</option>
                      <option value="Sadar-Katol Road">Sadar-Katol Road</option>
                      <option value="Kamal Chowk-Koradi Road">Kamal Chowk-Koradi Road</option>
                      <option value="Pratap Nagar-Wanadongri">Pratap Nagar-Wanadongri</option>
                      <option value="Wardha Road-Chinch Bhawan">Wardha Road-Chinch Bhawan</option>
                    </optgroup>
                    <optgroup label="Class 11-12/JEE/NEET/CET (4 Major Areas)">
                      <option value="Wardhman Nagar-Manewada">Wardhman Nagar-Manewada</option>
                      <option value="Kamal Chowk-Friends Colony">Kamal Chowk-Friends Colony</option>
                      <option value="Burdi-Wadi">Burdi-Wadi</option>
                      <option value="Dharampeth-Wardha Road">Dharampeth-Wardha Road</option>
                    </optgroup>
                    <optgroup label="Engineering">
                      <option value="Engineering (All Nagpur)">Engineering (All Nagpur)</option>
                    </optgroup>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Mobile Numbers *</label>
                {formData.mobileNumbers.map((mobile, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={mobile}
                      onChange={(e) => handleArrayChange(index, 'mobileNumbers', e.target.value)}
                      placeholder="Mobile number"
                      required={index === 0}
                    />
                    {index > 0 && (
                      <button type="button" className="btn btn-danger" onClick={() => removeArrayField(index, 'mobileNumbers')}>
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn btn-secondary" onClick={() => addArrayField('mobileNumbers')}>
                  + Add Mobile
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Educational Qualifications</label>
                  <textarea name="educationalQualifications" value={formData.educationalQualifications} onChange={handleChange} rows="2" />
                </div>
                <div className="form-group">
                  <label>Teaching Experience</label>
                  <textarea name="teachingExperience" value={formData.teachingExperience} onChange={handleChange} rows="2" />
                </div>
                <div className="form-group">
                  <label>Fluency in English</label>
                  <select name="fluencyInEnglish" value={formData.fluencyInEnglish} onChange={handleChange}>
                    <option value="Excellent">Excellent</option>
                    <option value="Good">Good</option>
                    <option value="Average">Average</option>
                    <option value="Poor">Poor</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Preferred Time</label>
                  <input type="text" name="preferredTime" value={formData.preferredTime} onChange={handleChange} placeholder="e.g., Evening 5-8 PM" />
                </div>
              </div>

              <div className="form-group">
                <label>Preferred Classes</label>
                {formData.preferredClasses.map((cls, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={cls}
                      onChange={(e) => handleArrayChange(index, 'preferredClasses', e.target.value)}
                      placeholder="e.g., 10th, 12th Science"
                    />
                    {index > 0 && (
                      <button type="button" className="btn btn-danger" onClick={() => removeArrayField(index, 'preferredClasses')}>
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn btn-secondary" onClick={() => addArrayField('preferredClasses')}>
                  + Add Class
                </button>
              </div>

              <div className="form-group">
                <label>Preferred Subjects</label>
                {formData.preferredSubjects.map((subject, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => handleArrayChange(index, 'preferredSubjects', e.target.value)}
                      placeholder="e.g., Mathematics, Physics"
                    />
                    {index > 0 && (
                      <button type="button" className="btn btn-danger" onClick={() => removeArrayField(index, 'preferredSubjects')}>
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn btn-secondary" onClick={() => addArrayField('preferredSubjects')}>
                  + Add Subject
                </button>
              </div>

              <div className="form-group">
                <label>Preferred Board(s)</label>
                <div style={{ display: 'flex', gap: '16px' }}>
                  {['State', 'CBSE', 'ICSE', 'Custom'].map(board => (
                    <label key={board} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={formData.preferredBoards.includes(board)}
                        onChange={() => handleBoardChange(board)}
                      />
                      {board}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Address</label>
                <textarea name="address" value={formData.address} onChange={handleChange} rows="2" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Date of Joining</label>
                  <input type="date" name="dateOfJoining" value={formData.dateOfJoining} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Rating</label>
                  <select name="rating" value={formData.rating} onChange={handleChange}>
                    <option value="">Select Rating</option>
                    <option value="Avg">Avg</option>
                    <option value="Regular">Regular</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Highfees">Highfees</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" name="ownVehicle" checked={formData.ownVehicle} onChange={handleChange} />
                    Own Vehicle
                  </label>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" name="registrationFeesPaid" checked={formData.registrationFeesPaid} onChange={handleChange} />
                    Registration Fees Paid
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>KYC Details</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.kycDetails.formSubmitted}
                      onChange={(e) => setFormData({...formData, kycDetails: {...formData.kycDetails, formSubmitted: e.target.checked}})}
                    />
                    Form Submitted
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.kycDetails.passportPhoto}
                      onChange={(e) => setFormData({...formData, kycDetails: {...formData.kycDetails, passportPhoto: e.target.checked}})}
                    />
                    Passport Photo
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.kycDetails.aadhaarFront}
                      onChange={(e) => setFormData({...formData, kycDetails: {...formData.kycDetails, aadhaarFront: e.target.checked}})}
                    />
                    AADHAAR Front
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.kycDetails.aadhaarBack}
                      onChange={(e) => setFormData({...formData, kycDetails: {...formData.kycDetails, aadhaarBack: e.target.checked}})}
                    />
                    AADHAAR Back
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Remarks</label>
                <textarea name="remarks" value={formData.remarks} onChange={handleChange} rows="2" placeholder="Any additional notes" />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Students Modal */}
      {viewingTutor && (
        <div className="modal-overlay" onClick={() => setViewingTutor(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <h2>Students of {viewingTutor.name}</h2>
            {viewingTutor.students?.length === 0 ? (
              <p>No students assigned yet.</p>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Student Name</th>
                      <th>Fees/Month</th>
                      <th>Start Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewingTutor.students?.map((student, idx) => (
                      <tr key={idx}>
                        <td>{student.studentName}</td>
                        <td>₹{student.feesPerMonth}</td>
                        <td>{student.dateOfClassStart ? new Date(student.dateOfClassStart).toLocaleDateString() : 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setViewingTutor(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tutors;
